import { Fragment, type FC, type ReactNode } from 'react';
import classnames from 'classnames';

import { FormattedMessage, FormattedNumber } from 'dibs-react-intl';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { Button } from 'dibs-elements/exports/Button.tsx';
import { TradeBenefitsTile } from './TradeBenefitsTile.tsx';

import {
    BASE_IMAGE_URL,
    CUSTOMER_SUPPORT_URL,
    NET_PRICE_PERCENTAGE,
    PURCHASE_PROTECTION_SECTION_ID,
} from './tradeBenefitsConstants.ts';
import { trackTradeBenefitsEvent } from './tradeBenefitsTracking.ts';

import sharedStyles from './TradeBenefitsSharedStyles.scss';
import styles from './TradeBenefitsTiles.scss';
import dibsCss from 'dibs-css';

const ItalicTitle: FC<{ children: ReactNode }> = ({ children }) => (
    <span className={classnames(sharedStyles.headingMediumItalic, styles.titleItalic)}>
        {children}
    </span>
);

const BoldText: FC<{ children: ReactNode }> = ({ children }) => (
    <span className={classnames(styles.boldText, dibsCss.sassyFontBodyTypeHeavy)}>{children}</span>
);

const keyedChunks = (chunks: ReactNode[]): ReactNode =>
    chunks.map((chunk, index) => <Fragment key={index}>{chunk}</Fragment>);

const richTextValues = {
    br: <br />,
    italic: (chunks: ReactNode[]) => <ItalicTitle>{keyedChunks(chunks)}</ItalicTitle>,
    bold: (chunks: ReactNode[]) => <BoldText>{keyedChunks(chunks)}</BoldText>,
};

type Tile = {
    key: string;
    trigger: string;
    title: ReactNode;
    description: ReactNode;
    imageUrl: string;
    ctaCopy: ReactNode;
    ctaUrl: string;
};

const getTradeServiceCtaCopy = ({
    isAssigned,
    isSilverPlus,
}: {
    isAssigned: boolean;
    isSilverPlus: boolean;
}): ReactNode => {
    if (isAssigned) {
        return (
            <FormattedMessage
                id="abt.TradeBenefitsTiles.tradeService.cta.assigned"
                defaultMessage="Contact Your Representative"
            />
        );
    }

    if (isSilverPlus) {
        return (
            <FormattedMessage
                id="abt.TradeBenefitsTiles.tradeService.cta.unassignedSilver"
                defaultMessage="Contact Us"
            />
        );
    }

    return (
        <FormattedMessage
            id="abt.TradeBenefitsTiles.tradeService.cta.unassignedBronze"
            defaultMessage="Contact Support"
        />
    );
};

const getTiles = ({
    contactUrl,
    isAssigned,
    isSilverPlus,
}: {
    contactUrl: string;
    isAssigned: boolean;
    isSilverPlus: boolean;
}): Tile[] => [
    {
        key: 'netPrice',
        trigger: 'net-price',
        title: (
            <FormattedMessage
                id="abt.TradeBenefitsTiles.netPrice.title"
                defaultMessage="Net Price{br}<italic>on Furniture & Art</italic>"
                values={{
                    br: richTextValues.br,
                    italic: richTextValues.italic,
                }}
            />
        ),
        description: (
            <FormattedMessage
                id="abt.TradeBenefitsTiles.netPrice.description"
                defaultMessage="<bold>Enjoy up to {percentage}</bold> in net price discounts on unique pieces from our exceptional supply of contemporary, vintage, and antique furniture and art."
                values={{
                    bold: richTextValues.bold,
                    percentage: (
                        <FormattedNumber style="percent" value={NET_PRICE_PERCENTAGE / 100} />
                    ),
                }}
            />
        ),
        imageUrl: `${BASE_IMAGE_URL}/net_price_tile.jpg`,
        ctaCopy: (
            <FormattedMessage
                id="abt.TradeBenefitsTiles.netPrice.cta"
                defaultMessage="Shop with Net Price Discounts"
            />
        ),
        ctaUrl: '/search/?mv=true&netTradePriceDiscount=30',
    },
    {
        key: 'tradeService',
        trigger: 'exceptional-service',
        title: (
            <FormattedMessage
                id="abt.TradeBenefitsTiles.tradeService.title"
                defaultMessage="Exceptional{br}<italic>Trade Service</italic>"
                values={{
                    br: richTextValues.br,
                    italic: richTextValues.italic,
                }}
            />
        ),
        description: (
            <FormattedMessage
                id="abt.TradeBenefitsTiles.tradeService.description"
                defaultMessage="<bold>Our teams act as an extension of yours,</bold> with Dedicated Account Representatives, Sourcing Specialists, and Trade Customer Support."
                values={{ bold: richTextValues.bold }}
            />
        ),
        imageUrl: `${BASE_IMAGE_URL}/exceptional_service_tile.jpg`,
        ctaCopy: getTradeServiceCtaCopy({ isAssigned, isSilverPlus }),
        ctaUrl: contactUrl,
    },
    {
        key: 'purchaseProtection',
        trigger: 'purchase-protection',
        title: (
            <FormattedMessage
                id="abt.TradeBenefitsTiles.purchaseProtection.title"
                defaultMessage="Expanded{br}<italic>Purchase Protection</italic>"
                values={{
                    br: richTextValues.br,
                    italic: richTextValues.italic,
                }}
            />
        ),
        description: (
            <FormattedMessage
                id="abt.TradeBenefitsTiles.purchaseProtection.description"
                defaultMessage="<bold>Shop with confidence</bold> knowing that your projects are backed by 1stDibs’ most comprehensive set of purchase protections. We handle the risks so you can stay focused on your client."
                values={{ bold: richTextValues.bold }}
            />
        ),
        imageUrl: `${BASE_IMAGE_URL}/purchase_protection_tile.jpg`,
        ctaCopy: (
            <FormattedMessage
                id="abt.TradeBenefitsTiles.purchaseProtection.cta"
                defaultMessage="Learn More"
            />
        ),
        ctaUrl: `#${PURCHASE_PROTECTION_SECTION_ID}`,
    },
];

const TradeBenefitsTiles: FC<{
    isTrade?: boolean;
    contactUrl?: string;
    isFirmless?: boolean;
    isAssigned?: boolean;
    isSilverPlus?: boolean;
}> = ({
    isTrade = false,
    contactUrl = CUSTOMER_SUPPORT_URL,
    isFirmless = false,
    isAssigned = false,
    isSilverPlus = false,
}) => {
    const tiles = getTiles({ contactUrl, isAssigned, isSilverPlus });
    return (
        <div className={classnames(styles.wrapper, dibsCss.pyXlarge)}>
            {isTrade && (
                <div
                    className={classnames(
                        styles.header,
                        dibsCss.flex,
                        dibsCss.justifyBetween,
                        dibsCss.itemsCenter,
                        dibsCss.mbLarge
                    )}
                >
                    <HeadingLevel>
                        {Heading => (
                            <Heading
                                className={classnames(dibsCss.textHeaderXLargeNormal, dibsCss.m0)}
                            >
                                <FormattedMessage
                                    id="abt.TradeBenefitsTiles.title"
                                    defaultMessage="Take Advantage of {break}<italic>Your Trade 1st Benefits</italic>"
                                    values={{
                                        italic: content => (
                                            <span className={dibsCss.sassyFontHeaderXLargeItalic}>
                                                {content}
                                            </span>
                                        ),
                                        break: <br className={styles.mobileBreak} />,
                                    }}
                                />
                            </Heading>
                        )}
                    </HeadingLevel>
                    {!isFirmless && (
                        <div
                            className={classnames(
                                styles.description,
                                dibsCss.flex,
                                dibsCss.itemsCenter,
                                dibsCss.gapMedium,
                                dibsCss.sassyFontBodyLarge,
                                dibsCss.textRight
                            )}
                        >
                            <FormattedMessage
                                id="abt.TradeBenefitsTiles.tradeDescription"
                                defaultMessage="Earn points with every purchase you make."
                            />
                            <Button
                                href="/collections/best-sellers/?sort=newest"
                                target="_blank"
                                style="circle"
                                type="standard"
                                onClick={() =>
                                    trackTradeBenefitsEvent({
                                        stepInteractionName: 'start shopping clicked',
                                        trigger: 'benefits carousel',
                                    })
                                }
                            >
                                <FormattedMessage
                                    id="abt.TradeBenefitsTiles.tradeButton"
                                    defaultMessage="Start Shopping"
                                />
                            </Button>
                        </div>
                    )}
                </div>
            )}
            <div className={classnames(styles.container, dibsCss.grid, dibsCss.gapMedium)}>
                {tiles.map(({ key, ...tile }) => (
                    <TradeBenefitsTile key={key} {...tile} isTrade={isTrade} />
                ))}
            </div>
        </div>
    );
};

export { TradeBenefitsTiles };
